import {
  endOfMonth,
  endOfQuarter,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  subMonths,
  subQuarters,
} from 'date-fns';

import { addDaysToDate, formatDate } from '@/util/dateUtils';

import { PRESET_OPTIONS } from './periodOptions';
import { NAMED_PERIODS, NamedPeriod, Period } from './types';

// Calendar-based presets resolve against "now" so they roll forward (unlike a fixed day count).
const resolveNamedPeriod = (period: NamedPeriod, now: Date): { start: Date; end: Date } => {
  switch (period) {
    case 'week_to_date':
      return { start: startOfWeek(now, { weekStartsOn: 1 }), end: now };
    case 'month_to_date':
      return { start: startOfMonth(now), end: now };
    case 'last_month': {
      const previous = subMonths(now, 1);
      return { start: startOfMonth(previous), end: endOfMonth(previous) };
    }
    case 'quarter_to_date':
      return { start: startOfQuarter(now), end: now };
    case 'last_quarter': {
      const previous = subQuarters(now, 1);
      return { start: startOfQuarter(previous), end: endOfQuarter(previous) };
    }
  }
};

/**
 * Builds a start/end date pair based on the supplied `period` relative to today.
 * Custom periods are returned untouched, while presets derive their values from now.
 *
 * @param startDate - Previously stored start date string.
 * @param endDate - Previously stored end date string.
 * @param period - Period definition indicating either a custom range or a preset with `days`.
 * @returns { startDate: string; endDate: string } The resolved range, always formatted as `yyyy-MM-dd`.
 */
export function getPeriodDates(
  startDate: string,
  endDate: string,
  period: Period
): { startDate: string; endDate: string } {
  const now = new Date();

  if (period.period) {
    // Named boundaries are local wall-clock dates; format them locally so the calendar day stays stable across timezones.
    const { start, end } = resolveNamedPeriod(period.period, now);
    return { startDate: format(start, 'yyyy-MM-dd'), endDate: format(end, 'yyyy-MM-dd') };
  }

  if (!('days' in period)) {
    // Custom period → just return the existing values
    return { startDate, endDate };
  }

  if (period.single) {
    const targetDate = addDaysToDate(now, -period.days);
    return {
      startDate: formatDate(targetDate, 'yyyy-MM-dd'),
      endDate: formatDate(targetDate, 'yyyy-MM-dd'),
    };
  }

  const startTargetDate = addDaysToDate(now, -period.days);
  return {
    startDate: formatDate(startTargetDate, 'yyyy-MM-dd'),
    endDate: formatDate(now, 'yyyy-MM-dd'),
  };
}

// The preset whose resolved range matches the given range, or `null` when the range is custom.
export const getSelectedPeriod = (startDate: string, endDate: string): Period | null =>
  PRESET_OPTIONS.find((period) => {
    const resolved = getPeriodDates(startDate, endDate, period);
    return resolved.startDate === startDate && resolved.endDate === endDate;
  }) ?? null;

export const getNamedPeriodTitleKey = (startDate: string, endDate: string): string | null => {
  const match = NAMED_PERIODS.find((period) => {
    const resolved = getPeriodDates('', '', { title_key: '', period });
    return resolved.startDate === startDate && resolved.endDate === endDate;
  });
  return match ? `analytics.periods.${match}` : null;
};

/**
 * Parses a `yyyy-MM-dd` string into a local `Date` object positioned at midnight.
 * Rejects falsy or badly formatted values by returning `null`.
 *
 * @param value - ISO-like date string to convert, e.g. `2025-06-15`.
 * @returns Corresponding `Date` instance or `null` when the input cannot be parsed.
 */
export const toLocalDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};
